import { ThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FormApi, Mutator, SubmissionErrors } from "final-form";
import setFieldTouched from "final-form-set-field-touched";
import { noop } from "lodash";
import { ContextType, PureComponent, ReactNode, createRef } from "react";
import { Field, Form } from "react-final-form";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import { FixedSaveBar, FloatedInput, SaveBarMode } from "components/form";
import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import FormMode from "constants/form-mode";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import Role from "constants/role";
import { AttachmentContext } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { actions$ } from "store/action-stream";
import { CRUDAction, RequestAction } from "store/entities/actions";
import { AGENT } from "store/entities/agents";
import { IAgent, IChangeAutoAssignmentSuccessPayload } from "store/entities/agents/interfaces";
import { ITeam } from "store/entities/teams";
import { IAction, IActionWithPayload } from "store/helper";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";
import { compose, email, maxLength, required } from "utils/validators";

import { AgentDetailsProfileAvatar } from "../AgentDetailsProfileAvatar";

export interface ICommonAgentFormProps {
  teams?: ITeam[];
  mode: FormMode;
  canManageAgentTeams: boolean;
  onCancel(): void;
}

interface IProps extends ICommonAgentFormProps {
  loginFieldManagementEnabled: boolean;
  unsavedChanges: boolean;
  initialFormValues: IAgent;
  agent: IAgent;
  onSubmit: (
    agent: IAgent,
    form: FormApi<IAgent, Partial<IAgent>>,
    onSaving: () => void,
    onSuccess: (errors?: SubmissionErrors) => void
  ) => void;
  children: ReactNode;
  paymentFailed: boolean;
  isSavingAgent: boolean;
  isSelfManagement: boolean;
}

interface IState {
  isProcessing: boolean;
}

const OverflownContainer = styled.div`
  overflow: auto;
  width: 100%;
  height: 100%;
  background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};
  position: relative;
`;

const Panel = styled.div`
  box-sizing: border-box;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  padding: 68px;
  border-radius: 8px;
  max-width: 720px;
  margin: 100px auto;
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  position: relative;
  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    padding: 68px 0 0;
    width: 90vw;
  }
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  fieldset {
    width: 100%;
    margin-bottom: 13px;
  }
`;

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const unsetWeeklyReportsDecorator = (form: FormApi) => {
  const { change } = form;

  // eslint-disable-next-line no-param-reassign, @typescript-eslint/explicit-function-return-type
  form.change = (name: string, value: unknown) => {
    if (name === "permission" && value !== Role.Owner) {
      change("settings.weeklyReports", false);
    } else if (name === "permission" && value === Role.Owner) {
      change("settings.weeklyReports", form.getState().initialValues.settings.weeklyReports);
    }

    change(name, value);
  };

  return noop;
};

class AgentFormBase extends PureComponent<IProps, IState> {
  // @ts-expect-error TS(2564): Property 'context' has no initializer and is not d... Remove this comment to see the full error message
  context: ContextType<typeof AttachmentContext>;

  static defaultProps = {
    teams: undefined,
  };

  constructor(props: IProps) {
    super(props);

    if (props.canManageAgentTeams && !props.teams) {
      throw new Error("`teams` prop must be passed when `canManageAgentTeams` is set to `true`");
    }

    this.state = {
      isProcessing: false,
    };
  }

  updateUnsubscribe = noop;

  componentDidMount(): void {
    this.updateUnsubscribe = pipe(
      actions$,
      filter((action: IAction) => {
        return (
          action.type === AGENT[CRUDAction.UPDATE][RequestAction.FAILURE] ||
          (this.props.isSelfManagement && action.type === AGENT.CHANGE_AUTO_ASSIGNMENT[RequestAction.SUCCESS])
        );
      }),
      subscribe((action: IAction | IActionWithPayload<string, IChangeAutoAssignmentSuccessPayload>) => {
        if (
          this.props.isSelfManagement &&
          action.type === AGENT.CHANGE_AUTO_ASSIGNMENT[RequestAction.SUCCESS] &&
          "payload" in action
        ) {
          this.form.change("autoassignment", action.payload.autoassignment);
          this.form.resetFieldState("autoassignment");
        }
        if (action.type === AGENT[CRUDAction.UPDATE][RequestAction.FAILURE]) this.setState({ isProcessing: false });
      })
    );
  }

  componentDidUpdate(): void {
    if (this.props.paymentFailed) {
      this.setState({ isProcessing: false });
    }
  }

  componentWillUnmount(): void {
    this.updateUnsubscribe();
  }

  overflowRef: HTMLDivElement | null = null;

  private formRef = createRef<HTMLFormElement>();
  // @ts-expect-error TS(2564)
  form: FormApi<IAgent, Partial<IAgent>>;

  private getMode = (pristine: boolean): SaveBarMode => {
    if (this.state.isProcessing || this.props.isSavingAgent) {
      return SaveBarMode.Saving;
    }

    const formState = this.form.getState();
    const dirtyFields = formState.dirtyFields;
    const isOnlyAutoAssignmentDirty = Object.keys(dirtyFields).length === 1 && dirtyFields.autoassignment;
    const isAutoAssignmentTouched = formState.touched?.autoassignment;
    const isAutoAssignmentChangedOutsideForm =
      formState.initialValues.autoassignment !== this.props.agent.autoassignment;

    const isPristine = this.props.isSelfManagement
      ? ((isAutoAssignmentChangedOutsideForm && isOnlyAutoAssignmentDirty) || pristine) && !isAutoAssignmentTouched
      : pristine;

    if (this.props.unsavedChanges || !isPristine) {
      return SaveBarMode.OfferSave;
    }

    return SaveBarMode.Hidden;
  };

  private handleSubmit = async (
    values: IAgent,
    form: FormApi<IAgent, Partial<IAgent>>,
    onSuccess?: (errors?: SubmissionErrors) => void
  ): Promise<void> => {
    this.props.onSubmit(
      { ...values, avatar: this.props.agent.avatar },
      form,
      () => {
        this.setState({ isProcessing: true });
      },
      (...args) => {
        this.setState({ isProcessing: false }, () => {
          onSuccess?.(...args);
        });
      }
    );
  };

  render(): ReactNode {
    const { initialFormValues, agent, loginFieldManagementEnabled, mode, children, paymentFailed, isSelfManagement } =
      this.props;
    const title = agent.name || "New agent";
    const { avatar, name: agentName, email: agentEmail } = agent;

    return (
      <ThemeProvider theme={legacyTheme}>
        <Grid focusedColumn={0}>
          <ViewStack>
            <ViewContainer
              title={title}
              leftNodes={<BackButton to="/agents">Back</BackButton>}
              placement="main-content"
            >
              <Form<IAgent>
                onSubmit={this.handleSubmit}
                initialValues={initialFormValues}
                // @ts-expect-error TS(2322)
                decorators={[unsetWeeklyReportsDecorator]}
                mutators={
                  { setFieldTouched } as {
                    [key: string]: Mutator<IAgent, object>;
                  }
                }
                render={({ handleSubmit, pristine, invalid, form }) => {
                  const { onCancel } = this.props;
                  const { isProcessing } = this.state;
                  this.form = form;

                  return (
                    <OverflownContainer
                      ref={ref => {
                        this.overflowRef = ref;
                      }}
                    >
                      <Panel>
                        <AgentDetailsProfileAvatar
                          avatar={avatar}
                          agentName={agentName || agentEmail}
                          isSelfManagement={isSelfManagement}
                        />
                        <form ref={this.formRef} onSubmit={handleSubmit}>
                          <FormSections>
                            <FieldsWrapper>
                              <Field
                                validate={compose(
                                  required("Name is required"),
                                  maxLength("Name is too long (240 characters)", 240)
                                )}
                                className="fs-mask"
                                // @ts-expect-error TS(2322)
                                component={FloatedInput}
                                name="name"
                                label="Name"
                                size="big"
                              />
                              {loginFieldManagementEnabled && (
                                <Field
                                  validate={compose(required("Email is required"), email("Email is incorrect"))}
                                  // @ts-expect-error TS(2322)
                                  component={FloatedInput}
                                  className="fs-mask"
                                  name="email"
                                  label="Email"
                                  disabled={mode === FormMode.Edit}
                                />
                              )}
                            </FieldsWrapper>
                            {children}
                          </FormSections>
                        </form>
                      </Panel>
                      <FixedSaveBar
                        mode={this.getMode(pristine)}
                        onSave={handleSubmit}
                        customSaveLabel={mode === FormMode.Edit ? "Save changes" : "Add an agent"}
                        saveDisabled={
                          isProcessing ||
                          invalid ||
                          this.context.isUploading() ||
                          (paymentFailed && agent.permission === Role.Viewer)
                        }
                        onCancel={onCancel}
                      />
                    </OverflownContainer>
                  );
                }}
              />
            </ViewContainer>
          </ViewStack>
        </Grid>
      </ThemeProvider>
    );
  }
}

AgentFormBase.contextType = AttachmentContext;

export default AgentFormBase;

const FormSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing10)};
`;
